import { proxy, subscribe } from 'valtio';

interface PageLoadingState {
  isHeroImageLoaded: boolean;
  isModeLoaded: boolean;
  hasMinLoadingTime: boolean;
  isPageReady: boolean;
  isHeroTitleReady: boolean;
  isHeroSubtitleReady: boolean;
  isHeroButtonReady: boolean;
  isHeroImageAnimationReady: boolean;
}

export const pageLoadingStore = proxy<PageLoadingState>({
  isHeroImageLoaded: false,
  isModeLoaded: false,
  hasMinLoadingTime: false,
  isPageReady: false,
  isHeroTitleReady: false,
  isHeroSubtitleReady: false,
  isHeroButtonReady: false,
  isHeroImageAnimationReady: false
});

// Simple computed property
export const updatePageReady = () => {
  pageLoadingStore.isPageReady =
    pageLoadingStore.isHeroImageLoaded &&
    pageLoadingStore.isModeLoaded &&
    pageLoadingStore.hasMinLoadingTime;
};

// Helper functions
export const setHeroImageLoaded = (loaded: boolean) => {
  pageLoadingStore.isHeroImageLoaded = loaded;
  updatePageReady();
};

export const setModeLoaded = (loaded: boolean) => {
  pageLoadingStore.isModeLoaded = loaded;
  updatePageReady();
};

export const setMinLoadingTime = (hasTime: boolean) => {
  pageLoadingStore.hasMinLoadingTime = hasTime;
  updatePageReady();
};

export const setHeroTitleReady = (ready: boolean) => {
  pageLoadingStore.isHeroTitleReady = ready;
};

export const setHeroSubtitleReady = (ready: boolean) => {
  pageLoadingStore.isHeroSubtitleReady = ready;
};

export const setHeroButtonReady = (ready: boolean) => {
  pageLoadingStore.isHeroButtonReady = ready;
};

export const setHeroImageAnimationReady = (ready: boolean) => {
  pageLoadingStore.isHeroImageAnimationReady = ready;
};

// Internal timer handle for the min loading gate
let minLoadingTimer: ReturnType<typeof setTimeout> | null = null;

// Reset all flags and restart the minimum loading timer
export const resetPageLoading = (minDelayMs: number = 400) => {
  // Clear any previous timer and reset gate
  if (minLoadingTimer) {
    clearTimeout(minLoadingTimer);
    minLoadingTimer = null;
  }
  setMinLoadingTime(false);

  // Reset all flags
  pageLoadingStore.isHeroImageLoaded = false;
  pageLoadingStore.isModeLoaded = false;
  pageLoadingStore.isPageReady = false;
  pageLoadingStore.isHeroTitleReady = false;
  pageLoadingStore.isHeroSubtitleReady = false;
  pageLoadingStore.isHeroButtonReady = false;
  pageLoadingStore.isHeroImageAnimationReady = false;

  // Restart timer
  minLoadingTimer = setTimeout(() => {
    setMinLoadingTime(true);
  }, minDelayMs);
};

// Initialize hero elements delay (staggered 100ms apart). Uses valtio's
// subscribe so we only react when the store changes — no 50ms polling loop.
//
// `unsubscribe` is initialized to a no-op so scheduleOnce can call it
// unconditionally without hitting a temporal-dead-zone ReferenceError
// when `isPageReady` is already true at mount (which happens on SPA
// navigations back to the homepage — store state persists across nav).
export const initializeHeroElementsDelay = () => {
  let scheduled = false;
  let unsubscribe: () => void = () => {};

  const scheduleOnce = () => {
    if (scheduled) return;
    if (!pageLoadingStore.isPageReady) return;
    scheduled = true;

    if (!pageLoadingStore.isHeroTitleReady) {
      setTimeout(() => setHeroTitleReady(true), 200);
    }
    if (!pageLoadingStore.isHeroSubtitleReady) {
      setTimeout(() => setHeroSubtitleReady(true), 300);
    }
    if (!pageLoadingStore.isHeroButtonReady) {
      setTimeout(() => setHeroButtonReady(true), 400);
    }
    unsubscribe();
  };

  // If already ready, fire immediately; otherwise subscribe.
  if (pageLoadingStore.isPageReady) {
    scheduleOnce();
    return;
  }
  unsubscribe = subscribe(pageLoadingStore, scheduleOnce);
};

// Initialize hero image animation delay (400ms after page is ready).
export const initializeHeroImageDelay = () => {
  let scheduled = false;
  let unsubscribe: () => void = () => {};

  const scheduleOnce = () => {
    if (scheduled) return;
    if (!pageLoadingStore.isPageReady) return;
    if (pageLoadingStore.isHeroImageAnimationReady) return;
    scheduled = true;
    setTimeout(() => setHeroImageAnimationReady(true), 400);
    unsubscribe();
  };

  if (pageLoadingStore.isPageReady) {
    scheduleOnce();
    return;
  }
  unsubscribe = subscribe(pageLoadingStore, scheduleOnce);
};
